import {
  InquiryWithContext,
  SupplierInquiry,
  TierPrice,
} from '@texas/api/endpoints/inquiryApi';
import { useMemo, useState } from 'react';

interface SelectedCell {
  inquiryId: number;
  tierId: number;
  quantity: number;
  currentPrice: number | null;
  label: string;
}

export function useInquiryTierPriceSelection(
  inquiries: Array<InquiryWithContext | SupplierInquiry>,
) {
  const [selectedTierPriceIds, setSelectedTierPriceIds] = useState<Set<number>>(
    new Set(),
  );

  const allTierPrices = useMemo(
    () => inquiries.flatMap((inquiry) => inquiry.tierPrices),
    [inquiries],
  );

  const selectedAcceptableTierPriceIds = useMemo(
    () =>
      allTierPrices
        .filter(
          (tierPrice) =>
            selectedTierPriceIds.has(tierPrice.id) && tierPrice.price != null,
        )
        .map((tierPrice) => tierPrice.id),
    [allTierPrices, selectedTierPriceIds],
  );

  const selectedLockedTierPriceIds = useMemo(
    () =>
      allTierPrices
        .filter(
          (tierPrice) =>
            selectedTierPriceIds.has(tierPrice.id) && isTierLocked(tierPrice),
        )
        .map((tierPrice) => tierPrice.id),
    [allTierPrices, selectedTierPriceIds],
  );

  const selectedReviewedTierPriceIds = useMemo(
    () =>
      allTierPrices
        .filter(
          (tierPrice) =>
            selectedTierPriceIds.has(tierPrice.id) && isTierLocked(tierPrice),
        )
        .map((tierPrice) => tierPrice.id),
    [allTierPrices, selectedTierPriceIds],
  );

  const selectedEditableTierPriceIds = useMemo(
    () =>
      allTierPrices
        .filter(
          (tierPrice) =>
            selectedTierPriceIds.has(tierPrice.id) && !isTierLocked(tierPrice),
        )
        .map((tierPrice) => tierPrice.id),
    [allTierPrices, selectedTierPriceIds],
  );

  const selectableTierPriceIds = useMemo(
    () => allTierPrices.map((tierPrice) => tierPrice.id),
    [allTierPrices],
  );

  function toggleCell(tierPriceId: number) {
    setSelectedTierPriceIds((prev) => {
      const next = new Set(prev);
      next.has(tierPriceId) ? next.delete(tierPriceId) : next.add(tierPriceId);
      return next;
    });
  }

  function toggleRow(inquiry: InquiryWithContext | SupplierInquiry) {
    toggleIds(inquiry.tierPrices.map((tierPrice) => tierPrice.id));
  }

  function toggleColumn(quantity: number) {
    toggleIds(
      allTierPrices
        .filter((tierPrice) => tierPrice.quantity === quantity)
        .map((tierPrice) => tierPrice.id),
    );
  }

  function selectAllPrices() {
    setSelectedTierPriceIds(new Set(selectableTierPriceIds));
  }

  function clearSelection() {
    setSelectedTierPriceIds(new Set());
  }

  function buildSelectedCells(): SelectedCell[] {
    return inquiries.flatMap((inquiry) => {
      const labelBase = [
        'matrixNodeXValue' in inquiry ? inquiry.matrixNodeXValue : null,
        'matrixNodeYValue' in inquiry ? inquiry.matrixNodeYValue : null,
      ]
        .filter(Boolean)
        .join(' / ');

      return inquiry.tierPrices
        .filter(
          (tierPrice) =>
            selectedTierPriceIds.has(tierPrice.id) && !isTierLocked(tierPrice),
        )
        .map((tierPrice) => ({
          inquiryId: inquiry.id,
          tierId: tierPrice.id,
          quantity: tierPrice.quantity,
          currentPrice: tierPrice.price ?? null,
          label: `${labelBase} @ ${tierPrice.quantity}`,
        }));
    });
  }

  function toggleIds(ids: number[]) {
    if (ids.length === 0) return;

    const allSelected = ids.every((id) => selectedTierPriceIds.has(id));
    setSelectedTierPriceIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  }

  return {
    selectedTierPriceIds,
    selectedAcceptableTierPriceIds,
    selectedLockedTierPriceIds,
    selectedReviewedTierPriceIds,
    selectedEditableTierPriceIds,
    selectableTierPriceIds,
    toggleCell,
    toggleRow,
    toggleColumn,
    selectAllPrices,
    clearSelection,
    buildSelectedCells,
  };
}

export function isTierLocked(tierPrice: TierPrice) {
  return tierPrice.acceptedDate != null || tierPrice.rejectedAt != null;
}
